import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FlashMessagesService } from 'flash-messages-angular';
import { Subject } from 'rxjs';
import { PropertiesService } from 'src/app/services/properties.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ClientPropertyDialogComponent } from './client-property-dialog/client-property-dialog.component';
import { DataTableDirective } from 'angular-datatables';
import { DatatablesHelper } from 'src/app/helpers';
import { ClientProperty } from 'src/app/models';
import { Router } from '@angular/router';

type UIProperty = ClientProperty & {
  saving?: boolean;
};

@Component({
  selector: 'app-client-properties-rw',
  templateUrl: './client-properties-rw.component.html',
  styleUrls: ['./client-properties-rw.component.css']
})
export class ClientPropertiesRwComponent implements OnInit, OnDestroy {
  properties: any[] = [];
  loaded = false;

  // Has to be 'any' for the Buttons extension to work,
  // the DataTables.Settings type doesn't recognize the configuration for the extensions
  dtOptions: any = {};

  // We use this trigger because fetching the properties can take a while,
  // thus we ensure the data is fetched before rendering
  dtTrigger: Subject<any> = new Subject<any>();

  @ViewChild(DataTableDirective, {static: false})
  dtElement!: DataTableDirective;

  constructor(private propertiesService: PropertiesService, private flashMessage: FlashMessagesService, private dialog: MatDialog, private router: Router) {}

  ngOnInit(): void {
    this.propertiesService.currentProperty = null;
    this.initTable();
    this.loadTable();
  }

  ngOnDestroy(): void {
    // Do not forget to unsubscribe the event
    this.dtTrigger.unsubscribe();
  }

  private initTable(): void {
    this.dtOptions = {
      pagingType: 'full_numbers',
      pageLength: 10,
      // enables horizontal scrolling on datatable
      scrollX: true,

      // Declare the use of the extension in the dom parameter and give a class for the css (can't get the css to apply though so commented out)
      // dom: '<"table-buttons"lBfr>tip',
      dom: 'lBfrtip',

      // Configure the buttons
      buttons: [
        'colvis',
        'excel'
      ]
    };
  }

  // Manually rerender table after changes
  private async loadTable() {
    // Get the properties for this user
    const res: any = await this.propertiesService.getSelfClientProperties({ getmoveinsurvey: true }).toPromise();

    // Assign the data
    this.properties = res.properties || [];

    if (this.loaded) {
      this.rerender();
    } else {
      this.loaded = true;

      // Calling the DT trigger to manually render the table
      this.dtTrigger.next();
    }
  }

  private rerender(): void {
    DatatablesHelper.redrawTable(this.dtElement, this.dtTrigger);
  }

  addPropertyDialog() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      title: 'Add Property',
    };

    const dialogRef = this.dialog.open(ClientPropertyDialogComponent, dialogConfig);

    dialogRef.afterClosed().toPromise().then((propertyInfo: any) => {
      if (!propertyInfo) return;

      // Add Property
      this.propertiesService.addClientProperty(propertyInfo).toPromise().then((res: any) => {

        // Success
        if (res.success) {
          // Display success
          this.flashMessage.show(res.message || "Property added.", {
            cssClass: 'alert-success',
            timeout: 3000
          });

          if (res.property?.isRented) {
            // Navigate to moved in inspection - checklist
            this.openChecklist(res.property);
          }
          else {
            // Rerender manually
            this.loadTable();
          }
        }
      });
    });
  }

  editProperty(property: any){
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      title: 'Edit Property',

      // Gotta send all the property data to the dialog to prefill
      date:         property.date,
      isRented:     property.isRented,
      rentAmount:   property.rentAmount,
      ownerName:    property.ownerName,
      managerName:  property.managerName,
      contactPhone: property.contactPhone,
      email:        property.email,
      address:      property.address,
      city:         property.city,
      zipCode:      property.zipCode,
      unitNumber:   property.unitNumber,
      bedrooms:     property.bedrooms,
      bathrooms:    property.bathrooms,
      latitude:     property.latitude,
      longitude:    property.longitude,
    };

    const dialogRef = this.dialog.open(ClientPropertyDialogComponent, dialogConfig);

    dialogRef.afterClosed().toPromise().then((propertyInfo: any) => {
      if (!propertyInfo) return;

      // Update Property
      this.propertiesService.updateClientProperty(property._id, propertyInfo).toPromise().then((res: any) => {

        // Success
        if (res.success) {
          // Display success
          this.flashMessage.show(res.message || "Property updated.", {
            cssClass: 'alert-success',
            timeout: 3000
          });

          if (res.property?.isRented && !res.property?.isMoveInInspectionChecklistCompleted) {
            // Navigate to moved in inspection - checklist
            this.openChecklist(Object.assign(property, res.property));
          }
          else {
            // Rerender manually
            this.loadTable();
          }
        }
      });
    });
  }

  openChecklist(property: any) {
    this.propertiesService.currentProperty = property;
    this.router.navigate(['/courses/RentWise/properties/condition-checklist'], { queryParams: { propertyid: property._id } })
  }

  deleteProperty(property: UIProperty){
    if (confirm(`Are you sure you want to permanently delete property from your list at address ${property.address}?`) !== true) {
      return;
    }

    // Delete Property
    this.propertiesService.deleteClientProperty(property._id).toPromise().then((data: any) => {

      // Rerender manually
      this.loadTable();

      // Success
      if (data.success) {
        // Display success
        this.flashMessage.show("Property deleted.", {
          cssClass: 'alert-success',
          timeout: 3000
        });
      }
    });
  }

  async saveRentedFlag(property: UIProperty) {
    const newValue = property.isRented;

    try {
      property.saving = true;
      await this.propertiesService.updateClientProperty(property._id, { isRented: newValue }).toPromise();
    } finally {
      property.saving = false;
    }
  }

  sortDate(value: any) {
    return value ? +new Date(value) : 0;
  }
}
